"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import {
  Package,
  MapPin,
  User,
  Phone,
  Clock,
  Shirt,
  ExternalLink,
  ChevronRight,
} from "lucide-react";

interface OrderQueueCardProps {
  order: {
    id: string;
    publicId: string;
    orderType: string;
    status: string;
    itemCount: number;
    customer: {
      publicId: string;
      name: string;
      phone?: string | null;
    };
    address: {
      line1: string;
      line2?: string | null;
      city: string;
      pincode: string;
    };
    area: {
      name: string;
    };
    pickupShipment?: {
      status: string;
      handoverTime?: string;
      slot: {
        startAt: string;
        endAt: string;
      };
    } | null;
    createdAt: string;
  };
  action?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

const statusLabels: Record<string, string> = {
  PICKED_UP: "Picked Up",
  RECEIVED_AT_WORKSHOP: "Received",
  INSPECTION_IN_PROGRESS: "Inspecting",
  QUOTE_SENT: "Quote Sent",
  AWAITING_PAYMENT: "Awaiting Payment",
  PAID: "Paid",
  IN_PRODUCTION: "In Production",
  QC: "Quality Check",
  READY_FOR_DELIVERY: "Ready",
  DELIVERY_ASSIGNED: "Delivery Assigned",
};

const statusColors: Record<string, string> = {
  PICKED_UP: "bg-teal-100 text-teal-700",
  RECEIVED_AT_WORKSHOP: "bg-blue-100 text-blue-700",
  INSPECTION_IN_PROGRESS: "bg-sky-100 text-sky-700",
  QUOTE_SENT: "bg-amber-100 text-amber-700",
  AWAITING_PAYMENT: "bg-yellow-100 text-yellow-700",
  PAID: "bg-green-100 text-green-700",
  IN_PRODUCTION: "bg-blue-100 text-blue-700",
  QC: "bg-sky-100 text-sky-700",
  READY_FOR_DELIVERY: "bg-emerald-100 text-emerald-700",
  DELIVERY_ASSIGNED: "bg-cyan-100 text-cyan-700",
};

export function OrderQueueCard({
  order,
  action,
  secondaryAction,
}: OrderQueueCardProps) {
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${order.address.line1}, ${order.address.city} ${order.address.pincode}`
  )}`;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold">{order.publicId}</span>
              </div>
              <Badge className={statusColors[order.status] || "bg-gray-100 text-gray-700"}>
                {statusLabels[order.status] || order.status}
              </Badge>
              <Badge variant="outline">
                {order.orderType === "ALTERATION" ? "Alteration" : "Stitching"}
              </Badge>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{order.customer.name}</span>
                <span className="text-muted-foreground">({order.customer.publicId})</span>
              </div>

              {order.customer.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{order.customer.phone}</span>
                </div>
              )}
            </div>

            <div className="flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <p>{order.address.line1}</p>
                {order.address.line2 && (
                  <p className="text-muted-foreground">{order.address.line2}</p>
                )}
                <p className="text-muted-foreground">
                  {order.address.city}, {order.address.pincode}
                </p>
              </div>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="h-3 w-3" />
                Map
              </a>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Shirt className="h-4 w-4 text-muted-foreground" />
                <span>{order.itemCount} item{order.itemCount !== 1 ? "s" : ""}</span>
              </div>

              {order.pickupShipment && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>
                    Pickup: {formatDateLong(order.pickupShipment.slot.startAt)}{" "}
                    {formatTimeRange(
                      order.pickupShipment.slot.startAt,
                      order.pickupShipment.slot.endAt
                    )}
                  </span>
                </div>
              )}

              {order.pickupShipment?.handoverTime && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>
                    Handed over: {formatDateLong(order.pickupShipment.handoverTime)}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {action && (
              <Button
                onClick={action.onClick}
                disabled={action.loading}
                size="sm"
              >
                {action.label}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {secondaryAction && (
              <Button
                variant="outline"
                onClick={secondaryAction.onClick}
                size="sm"
              >
                {secondaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
